import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Loader2, Save, ArrowLeft, Trash2, CheckCircle, ExternalLink, Wand2, Layers } from "lucide-react";
import { toast } from "sonner";
import CockpitLayout from "@/components/admin/CockpitLayout";
import ImageManager from "@/components/admin/ImageManager";
import ItemFields from "@/components/admin/ItemFields";
import { LocationSelect } from "@/components/admin/LocationSelect";
import ConfirmDialog from "@/components/admin/ConfirmDialog";
import { GoldAction, GoldLoading } from "@/components/admin/GoldUi";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { can } from "@/lib/perms";

export default function EditItem() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const canPublish = can(user, "item:publish");
  const canDelete = can(user, "item:delete");
  const [confirm, setConfirm] = useState(false);
  const [form, setForm] = useState(null);
  const [images, setImages] = useState([]);
  const [slug, setSlug] = useState("");
  const [saving, setSaving] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [locCtx, setLocCtx] = useState(null);
  const [locationId, setLocationId] = useState("");

  useEffect(() => {
    api.get(`/items/${id}`).then(({ data }) => {
      setForm({
        title: data.title || "", category: data.category || "Sonstiges", condition: data.condition || "Gut",
        price: data.price ?? null, description: data.description || "", seo_title: data.seo_title || "",
        seo_description: data.seo_description || "", whatsapp_text: data.whatsapp_text || "",
        status: data.status || "draft", ai_confidence: data.ai_confidence || 0,
      });
      setSlug(data.slug);
      if (data.location_id) setLocationId(data.location_id);
      setImages((data.raw_images || []).map((i) => ({ ...i, url: i.url, thumb_url: i.thumb_url })));
    }).catch(() => { toast.error("Fundstück nicht gefunden."); navigate("/dashboard/fundstuecke"); });
    api.get("/me/locations").then(({ data }) => { if (data.multi) setLocCtx(data); }).catch(() => {});
  }, [id, navigate]);

  const mainImage = images.find((i) => i.is_main) || images[0];

  const analyze = async () => {
    if (!mainImage) { toast.error("Kein Bild vorhanden."); return; }
    setAnalyzing(true);
    try {
      const { data } = await api.post("/ai/analyze", { image_id: mainImage.id });
      const it = data.items?.[0];
      if (it) {
        setForm((f) => ({ ...f, title: it.title || f.title, category: it.category || f.category, condition: it.condition || f.condition, price: it.suggested_price ?? f.price, description: it.description || f.description, seo_title: it.seo_title || f.seo_title, seo_description: it.seo_description || f.seo_description, whatsapp_text: it.whatsapp_text || f.whatsapp_text, ai_confidence: it.confidence || 0 }));
        toast.success("KI-Texte aktualisiert!");
      }
    } catch { toast.error("KI-Analyse fehlgeschlagen."); }
    finally { setAnalyzing(false); }
  };

  const save = async (overrideStatus) => {
    if (locCtx?.multi && !locationId) { toast.error("Bitte einen Standort wählen."); return; }
    setSaving(true);
    try {
      const f = overrideStatus ? { ...form, status: overrideStatus } : form;
      const payload = { ...f, ...(locCtx?.multi ? { location_id: locationId } : {}), images: images.map((i) => ({ id: i.id, original_path: i.original_path, image_path: i.image_path, thumb_path: i.thumb_path, width: i.width, height: i.height, is_main: i.is_main, quality: i.quality })) };
      const { data } = await api.put(`/items/${id}`, payload);
      setForm((prev) => ({ ...prev, status: data.status }));
      toast.success("Gespeichert!");
    } catch (e) { toast.error(e.response?.data?.detail || "Speichern fehlgeschlagen."); }
    finally { setSaving(false); }
  };

  const del = async () => {
    try {
      await api.delete(`/items/${id}`);
      toast.success("In den Papierkorb gelegt.", {
        action: can(user, "item:restore") ? { label: "Rückgängig", onClick: async () => { await api.post(`/items/${id}/restore`); toast.success("Wiederhergestellt."); } } : undefined,
      });
      navigate("/dashboard/fundstuecke");
    } catch (e) { toast.error(e.response?.data?.detail || "Löschen fehlgeschlagen."); }
  };

  if (!form) return <CockpitLayout active="fundstuecke" title="Fundstück bearbeiten"><GoldLoading label="Fundstück wird geladen …" /></CockpitLayout>;

  const isPublic = ["available", "reserved", "sold"].includes(form.status);

  return (
    <CockpitLayout active="fundstuecke" title="Fundstück bearbeiten">
      <div className="fundio-page-stack" data-testid="gold-edititem">
        <div className="fundio-section-head">
          <Link to="/dashboard/fundstuecke" className="fundio-text-link"><ArrowLeft size={18} aria-hidden="true" /> Fundstücke</Link>
          <div className="flex gap-2">
            {isPublic && <GoldAction href={`/fundstuecke/${slug}`} target="_blank" rel="noreferrer"><ExternalLink size={16} aria-hidden="true" /> Online ansehen</GoldAction>}
            {canDelete && <button data-testid="delete-item-btn" onClick={() => setConfirm(true)} className="fundio-action" style={{ color: "#903824", borderColor: "#E7B9AF" }}><Trash2 size={16} aria-hidden="true" /> Löschen</button>}
          </div>
        </div>

        <h1 className="fundio-page-title">Fundstück bearbeiten</h1>

        <div className="fundio-create-grid">
          <div className="fundio-create-photos">
            <div className="fundio-panel">
              <h2 className="fundio-section-title mb-4 flex items-center gap-2"><Layers size={20} aria-hidden="true" /> Fotos</h2>
              <ImageManager images={images} setImages={setImages} locationId={locCtx?.multi ? locationId : undefined} />
            </div>
            <GoldAction full data-testid="reanalyze-btn" onClick={analyze} disabled={analyzing || images.length === 0}>
              {analyzing ? <Loader2 size={18} className="animate-spin" /> : <Wand2 size={18} aria-hidden="true" />} KI-Texte neu generieren
            </GoldAction>
          </div>

          <div className="fundio-panel">
            <h2 className="fundio-section-title mb-4">Details</h2>
            {locCtx?.multi && (
              <div className="mb-4">
                <LocationSelect locations={locCtx.locations || []} value={locationId} onChange={setLocationId} required />
              </div>
            )}
            <ItemFields form={form} set={setForm} canPublish={canPublish} />
            <div className="mt-6 flex flex-wrap gap-2">
              <GoldAction variant="primary" full data-testid="save-edit-btn" onClick={() => save()} disabled={saving} style={{ flex: 1 }}>
                {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} aria-hidden="true" />} Speichern
              </GoldAction>
              {canPublish && form.status !== "available" && (
                <button data-testid="publish-btn" onClick={() => save("available")} disabled={saving}
                  className="fundio-action" style={{ background: "#1E7E45", color: "#fff", borderColor: "#1E7E45" }}>
                  <CheckCircle size={16} aria-hidden="true" /> Veröffentlichen
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
      <ConfirmDialog
        open={confirm}
        onOpenChange={setConfirm}
        title="In den Papierkorb legen?"
        description={form ? `„${form.title}" wird in den Papierkorb verschoben. 30 Tage wiederherstellbar.` : ""}
        confirmLabel="In Papierkorb"
        danger
        onConfirm={del}
      />
    </CockpitLayout>
  );
}
